import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import IcChevronUpRegular from 'src/assets/images/icChevronUpRegular.svg';
import { hoverAndActiveCss } from 'src/utils/styled';

/** 칩 높이(32px) + row gap(8px) + 칩 높이(32px) = 72px */
const COLLAPSED_MAX_HEIGHT = 72;
const ChipContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ChipWrap = styled.div<{
  isCollapsed: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 4px;
  overflow: hidden;
  ${({
  isCollapsed
}) => isCollapsed && `max-height: ${COLLAPSED_MAX_HEIGHT}px;`}
`;
const InterestChip = styled.button<{
  selected: boolean;
}>`
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  height: 32px;
  padding: 0 16px;
  border: none;
  border-radius: 360px;
  font-size: 14px;
  font-weight: 700;
  line-height: 32px;
  cursor: pointer;
  transition:
    background-color 0.15s,
    color 0.15s;
  background-color: ${({
  theme,
  selected
}) => selected ? theme.color.surface12 : theme.color.surface4};
  color: ${({
  theme,
  selected
}) => selected ? theme.color.contentBright : theme.color.content11};

  ${({
  theme,
  selected
}) => !selected && hoverAndActiveCss(`
      background-color: ${theme.color.surface5};
    `)}
`;
const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  height: 32px;
  gap: 4px;
  padding: 0px 12px;
  border-radius: 360px;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  cursor: pointer;
  color: ${({
  theme
}) => theme.color.content11};
  background-color: transparent;
  border: 1px solid ${({
  theme
}) => theme.color.surface5};

  ${({
  theme
}) => hoverAndActiveCss(`
      background-color: ${theme.color.surface3};
    `)}
`;
const IcChevron = styled(IcChevronUpRegular)<{
  isOpen: boolean;
}>`
  width: 16px;
  height: 16px;
  transform: ${({
  isOpen
}) => !isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
interface CategoryChipGroupProps {
  interests: {
    id: string;
    name: string;
  }[];
  selectedInterests: Set<string>;
  onToggle: (id: string) => void;
}
const CategoryChipGroup = ({
  interests,
  selectedInterests,
  onToggle
}: CategoryChipGroupProps) => {
  const {
    t
  } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const chipWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = chipWrapRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollHeight > COLLAPSED_MAX_HEIGHT);
  }, [interests]);
  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  return <ChipContainer data-sentry-element="ChipContainer" data-sentry-component="CategoryChipGroup" data-sentry-source-file="CategoryChipGroup.tsx">
      <ChipWrap ref={chipWrapRef} isCollapsed={!isExpanded} data-sentry-element="ChipWrap" data-sentry-source-file="CategoryChipGroup.tsx">
        {interests.map(interest => <InterestChip key={interest.id} selected={selectedInterests.has(interest.id)} onClick={() => onToggle(interest.id)}>
            {interest.name}
          </InterestChip>)}
      </ChipWrap>
      {isOverflowing && <ToggleButton onClick={handleToggleExpand}>
          {isExpanded ? t('profile_update__interests_button_show_less') : t('profile_update__interests_button_show_more')}
          <IcChevron isOpen={isExpanded} />
        </ToggleButton>}
    </ChipContainer>;
};
export default CategoryChipGroup;